import { useMemo } from "react";

import { getSpanToolName } from "../utils";

import { useUser } from "@/context/user-context";
import { useListToolRegistries } from "@/queries/tool-registry";
import type { TraceSpanLike } from "@/components/TraceDetail/SpanDetails/types";
import { routePaths } from "@/utils/routePaths";

const TOOL_ID_ATTRIBUTE_KEYS = [
  "pl.tool_id",
  "pl.tool_registry_id",
  "promptlayer.tool_id",
  "tool_id",
  "tool_registry_id",
] as const;

const getAttributeToolId = (span: TraceSpanLike): string | undefined => {
  for (const key of TOOL_ID_ATTRIBUTE_KEYS) {
    const value = span.attributes?.[key];
    if (typeof value === "number" && Number.isFinite(value)) {
      return String(value);
    }
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  return undefined;
};

export const usePromptLayerToolLink = (span: TraceSpanLike | null | undefined) => {
  const workspaceId = useUser()?.activeWorkspaceId;
  const toolName = span ? getSpanToolName(span) : undefined;
  const attributeToolId = span ? getAttributeToolId(span) : undefined;
  const shouldResolveByName = Boolean(workspaceId && toolName && !attributeToolId);
  const { data } = useListToolRegistries(shouldResolveByName ? (workspaceId ?? null) : null);

  return useMemo(() => {
    if (!workspaceId || !span || !toolName) {
      return null;
    }

    if (attributeToolId) {
      return {
        toolName,
        url: routePaths.workspaceTool(workspaceId, attributeToolId),
      };
    }

    const normalizedToolName = toolName.toLowerCase();
    const registryTool = data?.tool_registries.find(
      tool => tool.name.toLowerCase() === normalizedToolName,
    );

    if (!registryTool) {
      return null;
    }

    return {
      toolName: registryTool.name,
      url: routePaths.workspaceTool(workspaceId, registryTool.id),
    };
  }, [attributeToolId, data?.tool_registries, span, toolName, workspaceId]);
};
